import {
  BodyText,
  Highlight,
  LinkText,
  TabPanel,
} from '@postscript/components';
import DisplayCard from '../../Layout/DisplayCard';
import {
  StyledCardGrid,
  StyledTabPanelIntro,
} from '../../Layout/SimpleStyledComponents';
import { FoundationsProps } from '../Foundations';

const FoundationsContent = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-copy" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium">
          Here you&apos;ll find guidlines on speaking as our brand, including
          basic instruction from the Nielson Norman Group on making UI copy
          successful.{' '}
          <LinkText
            href="https://nngroup.com/articles/ui-copy"
            target="_blank"
            rel="noreferrer"
          >
            Source
          </LinkText>
        </BodyText>
      </StyledTabPanelIntro>

      <StyledCardGrid desktopColumns={3}>
        <DisplayCard
          heading="Short but clear CTAs"
          advancedDescription={
            <>
              <BodyText size="small">
                Use just enough text to make CTAs specific and actionable. Avoid
                articles and use no more than 5 words. Avoid possessive terms
                such as &quot;View My Automations&quot; or &quot;Delete Your
                Campaign&quot;.
              </BodyText>

              <BodyText size="small">
                Use{' '}
                <Highlight>
                  <strong>Title Case</strong>
                </Highlight>{' '}
                for CTAs.
              </BodyText>
            </>
          }
          emoji="crystal-ball"
        />

        <DisplayCard
          heading="Write mindful headings"
          advancedDescription={
            <>
              <BodyText size="small">
                A heading gives a summary of its content. Avoid punctuation,
                except when it adds clarity or makes an amusing point.
              </BodyText>

              <BodyText size="small">
                Use{' '}
                <Highlight>
                  <strong>Sentence case</strong>
                </Highlight>{' '}
                for headings.
              </BodyText>
            </>
          }
          emoji="woman-in-lotus-position-medium-skin-tone"
        />

        <DisplayCard
          heading="Consistent, specific command words"
          description={`Whether it's in a Dropdown Menu or a Button, a command
                should always be a consistent term. If you say "Upload Image" in
                one place, don't say "Choose File" in another if it's
                the same action.`}
          emoji="open-file-folder"
        />

        <DisplayCard
          heading="Consequent state &gt; Current state"
          description="Describe what the action WILL do, rather than what it is. A
                playing video has a Pause button, not an Is Playing button."
          emoji="pause-button"
        />

        <DisplayCard
          heading="Use action verbs"
          description={`Try to follow the active Verb &gt; Noun format for commands.
                "Next Slide" is clearer than "Next", and "Accept Terms &
                Conditions" is clearer than "Got It".`}
          emoji="person-running-dark-skin-tone"
        />

        <DisplayCard
          heading="Avoid vague or generic commands"
          description='Be explicit in your command names. Avoid "Download Now" or "See the Thing". Also avoid generic terms like "Okay," "Got It," and "Done."'
          emoji="face-in-clouds"
        />

        <DisplayCard
          heading="Adjectives"
          description="Use adjectives to indicate change in appearance or state. The
                adjective should always reflect how the element will change.
                For example, for text editing, adjectives such as, Regular,
                Bold, or Italic are used to describe the change in appearance."
          emoji="speak-no-evil-monkey"
        />

        <DisplayCard
          heading="Limit branded terms"
          description={`In-house or company-wide terminology for products or features
                may be clear to you, but a customer will not know what "Cheese"
                means (historical Postscript product codename), or what an "Exit Trigger"
                is without help. Try making a feature term clear, rather than explaining
                it in a tooltip.`}
          emoji="cheese-wedge"
        />

        <DisplayCard
          heading='Ellipsis mean "more"'
          description="Use an ellipsis (...) to indicate that more input or another
                action will follow."
          emoji="three-button-mouse"
          size="medium"
        />

        <DisplayCard
          heading="Provide keyboard shortcuts"
          description="Consider adding keyboard shortcuts to accomplish frequent
                tasks. Don't override common shortcuts (ie: cmd + s), but
                watch users for cues."
          emoji="keyboard"
        />

        <DisplayCard
          heading="Use tooltips sparingly"
          advancedDescription={
            <BodyText size="small">
              <LinkText
                href="https://components.postscript.io/?path=/docs/basic-tooltip--docs"
                target="_blank"
                rel="noreferrer"
              >
                Tooltips
              </LinkText>{' '}
              can be great to contextualize terms or explain difficult concepts.
              But they&apos;re also a crutch. Use them as a last resort or an
              occasional cherry. Try to resolve complexity via other UI/UX
              methods first, and avoid littering the interface.
            </BodyText>
          }
          emoji="woman-tipping-hand"
        />

        <DisplayCard
          heading="Clarify outcomes"
          description="Although tooltips are rarely the right answer, if a command is
                irreducibly complex, provide some form of description in the
                interface."
          emoji="teacher-light-skin-tone"
        />
      </StyledCardGrid>
    </TabPanel>
  );
};

export default FoundationsContent;
